import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import BlogCover from "../components/blog/BlogCover";
import SEOHead from "../components/SEOHead";
import { listBlogPosts, formatPostDate, postDate, postReadTime } from "../lib/blogApi";

function BlogCard({ post }) {
  const date = postDate(post);
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col"
      data-testid={`blog-card-${post.slug}`}
    >
      <BlogCover post={post} />
      <div className="mt-5 flex-1">
        <div className="flex items-center gap-3 text-xs text-moss-500">
          <time dateTime={date}>{formatPostDate(date)}</time>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {postReadTime(post)}
          </span>
        </div>
        <h2 className="font-display mt-2 text-xl sm:text-2xl font-extrabold text-moss-950 leading-snug group-hover:text-sage-700 transition-colors">
          {post.title}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-moss-700 leading-relaxed">
          {post.excerpt}
        </p>
        {post.author && (
          <p className="mt-3 text-xs text-moss-500">
            By <span className="font-semibold text-moss-800">{post.author}</span>
          </p>
        )}
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sage-700 group-hover:gap-2.5 transition-all">
          Read the piece
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

export default function BlogIndexPage() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    listBlogPosts()
      .then(setPosts)
      .catch((e) => setError(e.message || "Failed to load"));
  }, []);

  const [featured, ...rest] = posts || [];

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://binshare.co.uk/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://binshare.co.uk/blog" },
    ],
  };

  return (
    <div
      className="min-h-screen bg-cream-50 text-moss-900"
      data-testid="blog-index-page"
    >
      <SEOHead
        path="/blog"
        title="Waste Disposal Blog | Tips & Guides | Binshare UK"
        description="Expert guides on waste removal, bin collection services, and skip hire alternatives in the UK. Stay informed with Binshare's waste disposal blog."
        keywords="waste disposal blog UK, bin collection guides, skip hire alternative, waste removal tips, WEEE disposal, fly-tipping prevention"
        jsonLd={breadcrumbJsonLd}
      />
      <Navbar />
      <main className="pt-24 md:pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sage-100 text-sage-800 rounded-full text-[11px] font-semibold tracking-[0.14em] uppercase">
              The Binshare journal
            </div>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-moss-950 leading-[1.05] text-balance">
              Notes on the UK&apos;s{" "}
              <span className="text-sage-600">waste industry.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-moss-700 leading-relaxed max-w-2xl">
              Guides, opinion and small experiments from the team building
              Binshare — plus practical playbooks for homeowners, businesses
              and registered waste carriers. Every job comes with a receipt.
            </p>
          </div>

          {error && (
            <div
              className="mt-14 rounded-2xl border border-moss-100 bg-white p-8 text-moss-700"
              data-testid="blog-error"
            >
              Couldn&apos;t load posts right now. Please refresh.
            </div>
          )}

          {!posts && !error && (
            <div className="mt-14 grid md:grid-cols-2 gap-8 lg:gap-12" data-testid="blog-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[16/10] rounded-2xl bg-moss-100" />
                  <div className="mt-5 h-3 w-32 bg-moss-100 rounded" />
                  <div className="mt-3 h-6 w-3/4 bg-moss-100 rounded" />
                  <div className="mt-2 h-4 w-full bg-moss-100 rounded" />
                </div>
              ))}
            </div>
          )}

          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              className="mt-14 grid lg:grid-cols-12 gap-6 lg:gap-10 group items-center"
              data-testid={`blog-featured-${featured.slug}`}
            >
              <div className="lg:col-span-7">
                <BlogCover post={featured} />
              </div>
              <div className="lg:col-span-5">
                <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-sage-700">
                  Featured
                </p>
                <h2 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold text-moss-950 leading-tight group-hover:text-sage-700 transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-base md:text-lg text-moss-700 leading-relaxed">
                  {featured.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-3 text-sm text-moss-500">
                  <time dateTime={postDate(featured)}>
                    {formatPostDate(postDate(featured))}
                  </time>
                  <span aria-hidden>·</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {postReadTime(featured)}
                  </span>
                  {featured.author && (
                    <>
                      <span aria-hidden>·</span>
                      <span>By <span className="font-semibold text-moss-800">{featured.author}</span></span>
                    </>
                  )}
                </div>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sage-700 group-hover:gap-2.5 transition-all">
                  Read the piece
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          )}

          {rest.length > 0 && (
            <div className="mt-20">
              <div className="flex items-baseline justify-between mb-8">
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-moss-950">
                  More from the journal
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                {rest.map((p) => (
                  <BlogCard key={p.slug} post={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
